import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Divider, makeStyles, SvgIcon, Typography } from '@material-ui/core';
import PageContent from 'components/Generic/PageContent';
import { Routes } from 'routes';
import { TableFilterProvider } from 'tableFilterContext';
import { downloadFile, getImportReceptionsReport } from 'services/apiOperations';
import { formatFilterAsQueryParams } from 'utils/filters';
import { notify } from 'components/Notifier';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { AppContext } from 'context';
import Dialog from 'components/Dialog';
import { CharacterizationFilters, DialogWidth, Event } from 'types';
import client from 'services/client';
import CharacterizationList from './CharacterizationList';
import { ImportSuccessResponse } from './types';
import { DownloadIcon, ValidateIcon } from '../../components/Icons';
import ClosureErrorIcon from '../../components/Icons/ClosureErrorIcon';
import { useStyles } from './index.styles';

function downloadCharacterizations(
  sortingCenterId: string,
  filter: CharacterizationFilters,
  t: (m: string) => string,
  setModalVisible: (isVisible: boolean) => void,
): void {
  let url = `/sorting_centers/${sortingCenterId}/characterizations/report`;
  const params = new URLSearchParams({
    ...formatFilterAsQueryParams(filter, 'samplingDate'),
    sortingCenter: sortingCenterId,
  });

  if (!!params.toString()) {
    url += '?' + params.toString();
  }
  setModalVisible(true);
  downloadFile(url)
    .then(() => {
      setModalVisible(false);
      notify({
        message: t('characterizations.exportPage.success'),
        severity: 'success',
      });
    })
    .catch(() => {
      notify({
        message: t('genericError'),
        severity: 'error',
      });
      setModalVisible(false);
    });
}

const CharacterizationListPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [filter, setFilter] = useState<CharacterizationFilters | null>({});
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const [isExportDisabled, setExportDisabled] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [downloadVisible, setDownloadVisible] = useState(false);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = transformObjectId(sortingCenter);

  const onExport = () => {
    if (!sortingCenterId) return;
    downloadCharacterizations(sortingCenterId, filter ?? {}, t, setDownloadModalVisible);
  };

  const onImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!sortingCenter || !e.target.files) return;

    setDialogOpen(true);
    setLoading(true);
    setSuccess(false);
    setErrors([]);
    client
      .postFile(`/sorting_centers/${sortingCenterId}/characterizations/import`, e.target.files[0])
      .then((res: ImportSuccessResponse) => {
        setLoading(false);
        setSuccess(true);
        notify({
          message: t('characterizations.import.successMessage', { numberOfLines: res.count }),
          severity: 'success',
        });
        PubSub.publish(Event.REFRESH_CHARACTERIZATIONS);
      })
      .catch(err => {
        const body = err.response?.body;
        setLoading(false);
        setSuccess(false);
        setErrors(body.errors);
      });
    e.target.value = '';
  };

  const closeDialog = () => {
    setDialogOpen(false);
    if (!success) {
      notify({ message: t('characterizations.import.errorMessage'), severity: 'error' });
    }
  };

  const downloadErrorReport = () => {
    const errorString = errors.join('<br/>');
    getImportReceptionsReport(sortingCenterId, errorString).then(() => {
      setDialogOpen(false);
      notify({ message: t('characterizations.import.reportDownloaded'), severity: 'success' });
    });
  };

  return (
    <PageContent
      importAccept={'.xlsx'}
      title={t('characterizations.entityName')}
      actionButtonLink={{ pathname: Routes.CharacterizationCreate, state: { referer: Routes.Characterizations } }}
      noPadding
      exportButtonHandler={onExport}
      exportButtonDisabled={isExportDisabled}
      importButtonHandler={onImport}
    >
      <Dialog
        closable={success}
        isOpen={dialogOpen}
        width={DialogWidth.LARGE_FORM}
        onClose={closeDialog}
        renderButton={() => {}}
        title={
          loading ? (
            <Typography className={classes.title}>{t('characterizations.import.analysisInProgressTitle')}</Typography>
          ) : success ? (
            <Typography className={classes.title}>{t('characterizations.import.successTitle')}</Typography>
          ) : (
            <div className={classes.errorContainer}>
              <ClosureErrorIcon className={classes.errorIcon} />
              <Typography className={classes.errorTitle}>{t('characterizations.import.failedTitle')}</Typography>
            </div>
          )
        }
        renderContent={() => (
          <div style={{ padding: '15px' }}>
            {loading && (
              <>
                <Typography>{t('characterizations.import.analysisInProgressMessage')}</Typography>
                <CircularProgress style={{ marginTop: 10 }} />
              </>
            )}
            {!loading && success && (
              <div className={classes.dialogMessage}>
                <SvgIcon component={ValidateIcon} color="primary" className={classes.icon} />
                <Button onClick={closeDialog}>{t('close')}</Button>
              </div>
            )}
            {!loading && errors.length > 0 && (
              <>
                <Divider className={classes.tableDivider} />
                {errors.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
                <Divider className={classes.tableDivider} />

                <Button variant="outlined" className={classes.closeButton} onClick={closeDialog}>
                  {t('close')}
                </Button>
                <Button
                  className={classes.downloadButton}
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadErrorReport}
                >
                  {t('characterizations.import.download')}
                </Button>
              </>
            )}
          </div>
        )}
      />
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={downloadModalVisible}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <>
            <h1
              style={{
                fontWeight: 600,
                fontSize: 24,
              }}
            >
              {t('characterizations.exportPage.title')}
            </h1>
            <h2 className={classes.downloadModalSubtitle}>{t('characterizations.exportPage.subtitle')}</h2>
            <CircularProgress style={{ marginBottom: 10 }} size={49} thickness={6} />
          </>
        )}
      />
      <TableFilterProvider>
        <CharacterizationList filter={filter} setFilter={setFilter} setExportDisabled={setExportDisabled} />
      </TableFilterProvider>
    </PageContent>
  );
};
export default CharacterizationListPage;
